/**
 * skeletonKeys — Stable placeholder key arrays for skeleton .map() patterns.
 *
 * Promoted to @plerity/dashboard-shared from byte-identical versions in
 * finance (src/utils/skeleton.ts) and governance (src/utils/skeleton.ts).
 *
 * These constants supply React `.key` props for lists of skeleton items
 * rendered while data loads.  Using fixed string keys (rather than array
 * indices) prevents React from re-mounting siblings when the real data
 * arrives and the list length is stable.
 *
 * Usage:
 * ```tsx
 * import { SKELETON_4 } from '@plerity/dashboard-shared/widgets';
 *
 * {SKELETON_4.map((key) => (
 *   <MetricCardSkeleton key={key} />
 * ))}
 * ```
 *
 * The `SKELETON_KEYS` record lets you look up arrays by count when the
 * number of items is dynamic:
 * ```tsx
 * const keys = SKELETON_KEYS[count] ?? SKELETON_KEYS[4];
 * ```
 */

export const SKELETON_3: readonly string[] = ["s0", "s1", "s2"];
export const SKELETON_4: readonly string[] = ["s0", "s1", "s2", "s3"];
export const SKELETON_5: readonly string[] = ["s0", "s1", "s2", "s3", "s4"];
export const SKELETON_6: readonly string[] = ["s0", "s1", "s2", "s3", "s4", "s5"];
export const SKELETON_8: readonly string[] = ["s0", "s1", "s2", "s3", "s4", "s5", "s6", "s7"];
export const SKELETON_10: readonly string[] = ["s0", "s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8", "s9"];
export const SKELETON_12: readonly string[] = [
  "s0",
  "s1",
  "s2",
  "s3",
  "s4",
  "s5",
  "s6",
  "s7",
  "s8",
  "s9",
  "s10",
  "s11",
];

/**
 * Record mapping common list lengths to their skeleton key arrays.
 * Falls back to SKELETON_4 for any length not listed.
 */
export const SKELETON_KEYS: Readonly<Record<number, readonly string[]>> = {
  3: SKELETON_3,
  4: SKELETON_4,
  5: SKELETON_5,
  6: SKELETON_6,
  8: SKELETON_8,
  10: SKELETON_10,
  12: SKELETON_12,
} as const;
